import React from 'react';
import { map } from 'lodash';
import { Title } from '@plone/volto/components';
import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';
import type { Content as PloneContent } from '@plone/types';
import {
  FIELD_MAP_MAIN,
  FIELD_MAP_TRANSLATION,
  ModuleViewProps,
  getUiLabel,
} from './ModuleViewUtil';

const ModuleView: React.FC<ModuleViewProps> = ({ content }) => {
  const queryLang =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('show_language')
      : null;

  const mainContentLang = content.hauptsprache?.toLowerCase() || 'de';
  const currentLang = queryLang || mainContentLang;
  const getTranslatedFieldValue = (
    genericFieldName: string,
  ): string | null | undefined => {
    const useTranslationMap =
      currentLang !== mainContentLang &&
      currentLang === (content.uebersetzung?.toLowerCase() || null);
    const mapToUse = useTranslationMap ? FIELD_MAP_TRANSLATION : FIELD_MAP_MAIN;

    const actualFieldName = mapToUse[genericFieldName];

    if (actualFieldName && content.hasOwnProperty(actualFieldName)) {
      return content[actualFieldName];
    } else if (
      // Fallback to main language field if specific language field is missing
      FIELD_MAP_MAIN[genericFieldName] &&
      content.hasOwnProperty(FIELD_MAP_MAIN[genericFieldName])
    ) {
      return content[FIELD_MAP_MAIN[genericFieldName]];
    }
    return undefined;
  };

  interface RenderFieldProps {
    label: string;
    value: string | boolean | number | null | undefined;
    renderHTML?: boolean;
  }

  const RenderField: React.FC<RenderFieldProps> = ({
    label,
    value,
    renderHTML = false,
  }) => {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === '-' ||
      value === '0.0' ||
      value === 0
    ) {
      return null;
    }
    const displayValue =
      typeof value === 'boolean' ? value.toString() : String(value);
    return (
      <div className="module-field-row">
        <dt className="module-field-label">{label}</dt>
        {renderHTML ? (
          <dd
            className="module-field-value html-content"
            dangerouslySetInnerHTML={{ __html: displayValue }}
          />
        ) : (
          <dd className="module-field-value">{displayValue}</dd>
        )}
      </div>
    );
  };

  const formatSpecialNummer = (
    nummer: string | null | undefined,
  ): string | null | undefined => {
    if (!nummer || typeof nummer !== 'string') return nummer;
    const lastDashIndex = nummer.lastIndexOf('-');
    const lastDotIndex = nummer.lastIndexOf('.');
    if (
      lastDashIndex !== -1 &&
      lastDotIndex !== -1 &&
      lastDotIndex > lastDashIndex + 1
    ) {
      return nummer.slice(lastDashIndex + 2, lastDotIndex);
    }
    return '';
  };

  const pageTitle = getTranslatedFieldValue('Bezeichnung') || content.title;
  const translationLang = content.uebersetzung?.toLowerCase() || null;

  const normalizeStringForLabelLookup = (
    str: string | null | undefined,
  ): string => {
    return str ? str.toLowerCase().replace(/\s+/g, '-') : '';
  };

  return (
    <div id="module-view">
      <article id="page-document" className="module-view has-aside">
        <section className="content">
          <div className="blocks-group-wrapper transparent">
            <Title title={pageTitle} />
            {content.description && (
              <p className="documentDescription">{content.description}</p>
            )}

            <div className="module-details">
              {' '}
              <RenderField
                label={getUiLabel('number', currentLang)}
                value={formatSpecialNummer(content.Nummer)}
              />
              <RenderField
                label={getUiLabel('leading', currentLang)}
                value={getTranslatedFieldValue('LeitungText')}
                renderHTML={true}
              />
              <RenderField
                label={getUiLabel('ects', currentLang)}
                value={getTranslatedFieldValue('ECTS')}
              />
              {content.Unterrichtssprache && (
                <RenderField
                  label={getUiLabel('teachingLanguage', currentLang)}
                  value={
                    getUiLabel(
                      normalizeStringForLabelLookup(content.Unterrichtssprache),
                      currentLang,
                    ) || content.Unterrichtssprache
                  }
                />
              )}
              {map(
                Array.from({ length: 13 }, (_, i) => i + 1),
                (i) => {
                  const labelValue = getTranslatedFieldValue(`Label${i}`);
                  const memoValue = getTranslatedFieldValue(`Memo${i}`);
                  if (
                    labelValue &&
                    memoValue !== null &&
                    memoValue !== undefined &&
                    memoValue !== ''
                  ) {
                    return (
                      <RenderField
                        key={`memo-pair-${i}`}
                        label={String(labelValue)}
                        value={memoValue}
                        renderHTML={true}
                      />
                    );
                  }
                  return null;
                },
              )}
              {(() => {
                const labelValue = getTranslatedFieldValue('LabelBemerk');
                const memoValue = getTranslatedFieldValue('MemoBemerk');
                if (
                  labelValue &&
                  memoValue !== null &&
                  memoValue !== undefined &&
                  memoValue !== ''
                ) {
                  return (
                    <RenderField
                      key="memo-pair-bemerk"
                      label={String(labelValue)}
                      value={memoValue}
                      renderHTML={true}
                    />
                  );
                }
                return null;
              })()}
              {currentLang === 'en' && content.LabelE14 && content.MemoE14 && (
                <RenderField
                  key="memo-pair-14"
                  label={content.LabelE14}
                  value={content.MemoE14}
                  renderHTML={true}
                />
              )}
            </div>
            {translationLang && (
              <div className="language-switch">
                <a
                  href={`?show_language=${
                    currentLang === mainContentLang
                      ? translationLang
                      : mainContentLang
                  }`}
                >
                  (
                  {currentLang === mainContentLang
                    ? getUiLabel('switchToEnglish', currentLang)
                    : getUiLabel('switchToGerman', currentLang)}
                  )
                </a>
              </div>
            )}
          </div>
        </section>
        <aside className="sidebar-nav-right-wrapper">
          <SlotRenderer
            name="sidebar-nav-right"
            content={content as PloneContent}
          />
        </aside>
      </article>
    </div>
  );
};

export default ModuleView;
